import { useState } from 'react'
import { Sparkles, Loader2, CalendarClock } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { useGeneratePost, useSchedulePost } from './use-content'
import type { ContentType, ContentTone, Post } from './types'
import { CONTENT_TYPE_LABELS, TONE_LABELS } from './types'

/**
 * Default schedule value for the datetime-local input: 1 hour from now,
 * formatted as `YYYY-MM-DDTHH:mm` in the user's local timezone (datetime-local
 * has no timezone — the browser interprets it as local).
 */
function defaultScheduleValue(): string {
  const d = new Date(Date.now() + 60 * 60 * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

type GeneratePostDialogProps = {
  readonly open: boolean
  readonly onClose: () => void
  readonly onGenerated: (post: Post) => void
}

const CONTENT_TYPES: readonly ContentType[] = [
  'thought_leadership',
  'pain_point',
  'social_proof',
  'case_study',
  'cta',
]

const CONTENT_TONES: readonly ContentTone[] = [
  'professional',
  'conversational',
  'bold',
  'inspirational',
]

export function GeneratePostDialog({ open, onClose, onGenerated }: GeneratePostDialogProps) {
  const [topic, setTopic] = useState('')
  const [contentType, setContentType] = useState<ContentType>('thought_leadership')
  const [tone, setTone] = useState<ContentTone>('professional')
  const [scheduleEnabled, setScheduleEnabled] = useState(false)
  const [scheduleAt, setScheduleAt] = useState<string>(defaultScheduleValue())
  const [error, setError] = useState<string | null>(null)

  const { mutateAsync: generatePost, isPending: isGenerating } = useGeneratePost()
  const { mutateAsync: schedulePost, isPending: isScheduling } = useSchedulePost()
  const isPending = isGenerating || isScheduling

  function handleClose() {
    if (isPending) return
    setTopic('')
    setScheduleEnabled(false)
    setScheduleAt(defaultScheduleValue())
    setError(null)
    onClose()
  }

  async function handleGenerate() {
    if (!topic.trim()) {
      setError('Please enter a topic before generating.')
      return
    }
    if (scheduleEnabled) {
      const ts = new Date(scheduleAt).getTime()
      if (Number.isNaN(ts) || ts <= Date.now()) {
        setError('Schedule time must be in the future.')
        return
      }
    }
    setError(null)
    try {
      const post = await generatePost({ topic: topic.trim(), content_type: contentType, tone })
      // If scheduling was requested, immediately mark the generated post as
      // scheduled. The publisher workflow polls /content/schedule/due so this
      // is what makes it actually go out at the chosen time.
      let finalPost = post
      if (scheduleEnabled) {
        const iso = new Date(scheduleAt).toISOString()
        finalPost = await schedulePost({ id: post.id, scheduledAt: iso })
      }
      onGenerated(finalPost)
      setTopic('')
      setScheduleEnabled(false)
      setScheduleAt(defaultScheduleValue())
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed. Please try again.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Generate LinkedIn Post
          </DialogTitle>
          <DialogDescription>
            Describe your topic and pick a format. AI will draft a ready-to-approve post.
          </DialogDescription>
        </DialogHeader>

        {/* 2026-07-27: the Pro PlanGate is gone — every plan can generate.
            The per-plan weekly cap (Basic 5 / Pro 10) is enforced server-side
            and surfaces here as the 429 error message. */}
        <div className="space-y-4 pt-2">
          {/* Topic */}
          <div className="space-y-1.5">
            <Label htmlFor="gen-topic">Topic</Label>
            <Input
              id="gen-topic"
              placeholder="e.g. Why most cold outreach fails"
              value={topic}
              onChange={(e) => {
                setTopic(e.target.value)
                if (error) setError(null)
              }}
              onKeyDown={(e) => e.key === 'Enter' && !isPending && void handleGenerate()}
              disabled={isPending}
              maxLength={200}
            />
          </div>

          {/* Content type */}
          <div className="space-y-2">
            <Label>Content Type</Label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {CONTENT_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setContentType(type)}
                  disabled={isPending}
                  className={`rounded-md border px-3 py-2 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 ${
                    contentType === type
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-background text-foreground hover:bg-accent'
                  }`}
                >
                  {CONTENT_TYPE_LABELS[type]}
                </button>
              ))}
            </div>
          </div>

          {/* Tone */}
          <div className="space-y-2">
            <Label>Tone</Label>
            <div className="grid grid-cols-2 gap-2">
              {CONTENT_TONES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTone(t)}
                  disabled={isPending}
                  className={`rounded-md border px-3 py-2 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 ${
                    tone === t
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-background text-foreground hover:bg-accent'
                  }`}
                >
                  {TONE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          {/* Schedule (optional) */}
          <div className="space-y-2 rounded-md border p-3">
            <label className="flex cursor-pointer items-start gap-2">
              <Checkbox
                checked={scheduleEnabled}
                onCheckedChange={(v) => setScheduleEnabled(v === true)}
                disabled={isPending}
                className="mt-0.5"
              />
              <div className="flex flex-1 flex-col gap-0.5">
                <span className="flex items-center gap-1.5 text-sm font-medium">
                  <CalendarClock className="h-3.5 w-3.5" />
                  Schedule for later
                </span>
                <span className="text-xs text-muted-foreground">
                  Otherwise the post is saved as a draft for you to approve and publish manually.
                </span>
              </div>
            </label>
            {scheduleEnabled && (
              <Input
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                disabled={isPending}
                className="text-sm"
              />
            )}
          </div>

          {/* Error */}
          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}

          {/* Loading state */}
          {isPending && (
            <div className="flex items-center justify-center gap-2 rounded-md border border-primary/30 bg-primary/5 py-4">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">
                {isScheduling ? 'Scheduling…' : 'Generating…'}
              </span>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button onClick={() => void handleGenerate()} disabled={isPending || !topic.trim()} className="gap-2">
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {isScheduling ? 'Scheduling…' : 'Generating…'}
                </>
              ) : scheduleEnabled ? (
                <>
                  <CalendarClock className="h-4 w-4" />
                  Generate &amp; Schedule
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Generate Post
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
